/**
 * The vendor's tenants (container, phase 7b): the table, "new tenant", and
 * suspend / reactivate through one dialog. Live through the `tenant` change
 * kind (the cloud mirrors every tenant's footprint changes to the vendor).
 */
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { useCreateTenant, useReactivateTenant, useSuspendTenant, useVendorTenants } from '../app/hooks.js';
import { CreateTenantDialog } from '../components/vendor/CreateTenantDialog.js';
import { SuspendTenantDialog } from '../components/vendor/SuspendTenantDialog.js';
import { TenantsTable, vendorTenantHref } from '../components/vendor/TenantsTable.js';
import { useVendorAdmin } from './VendorLayout.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export type TenantStatusAction = { readonly mode: 'suspend' | 'reactivate'; readonly tenantId: string } | null;

/** Suspend and reactivate, with the dialog's state: shared by the list and a tenant's page. */
export function useTenantStatusActions() {
  const suspend = useSuspendTenant();
  const reactivate = useReactivateTenant();
  const [action, setAction] = useState<TenantStatusAction>(null);
  const failure = suspend.error ?? reactivate.error;
  const confirm = (text: string): void => {
    if (action === null) return;
    const done = () => {
      setAction(null);
      toast.success(action.mode === 'suspend' ? `Suspended ${action.tenantId}` : `Reactivated ${action.tenantId}`);
    };
    if (action.mode === 'suspend') suspend.mutate({ tenantId: action.tenantId, reason: text }, { onSuccess: done });
    else reactivate.mutate({ tenantId: action.tenantId, note: text }, { onSuccess: done });
  };
  const open = (mode: 'suspend' | 'reactivate', tenantId: string): void => {
    suspend.reset();
    reactivate.reset();
    setAction({ mode, tenantId });
  };
  return { action, open, close: () => setAction(null), confirm, pending: suspend.isPending || reactivate.isPending, error: failure === null ? null : messageOf(failure) };
}

export function VendorTenantsView() {
  const tenants = useVendorTenants();
  const create = useCreateTenant();
  const canAdmin = useVendorAdmin();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const status = useTenantStatusActions();
  const named = status.action === null ? null : (tenants.data ?? []).find((t) => t.tenantId === status.action?.tenantId);

  return (
    <>
      <TenantsTable
        tenants={tenants.data ?? []}
        loading={tenants.isFetching}
        error={tenants.error === null ? null : messageOf(tenants.error)}
        canAdmin={canAdmin}
        onCreate={() => {
          create.reset();
          setCreating(true);
        }}
        onSuspend={(id) => status.open('suspend', id)}
        onReactivate={(id) => status.open('reactivate', id)}
        onOpen={(id) => void navigate({ to: vendorTenantHref(id) })}
        onRetry={() => void tenants.refetch()}
      />
      <CreateTenantDialog
        open={creating}
        onOpenChange={setCreating}
        pending={create.isPending}
        error={create.error === null ? null : messageOf(create.error)}
        onSubmit={(request) =>
          create.mutate(request, {
            onSuccess: (made) => {
              setCreating(false);
              toast.success(`Created ${made.tenantId}; its admin is ${made.adminEmail}`);
            },
          })
        }
      />
      <SuspendTenantDialog
        mode={status.action?.mode ?? null}
        tenant={status.action === null ? null : { tenantId: status.action.tenantId, name: named?.name ?? status.action.tenantId }}
        pending={status.pending}
        error={status.error}
        onClose={status.close}
        onConfirm={status.confirm}
      />
    </>
  );
}
